import { useEffect, useRef, useMemo } from 'react';
import { generateHashatar, type RGBGrid } from '@hashatar/core';

export interface AnimatedAvatarProps {
  text: string;
  /** Number of frames per transition (default: 20) */
  transitionFrames?: number;
  /** Animation speed multiplier (default: 1.0) */
  animationSpeed?: number;
  /** Size of the avatar (default: 32) */
  size?: number | undefined;
  /** Style (additional styles for the canvas) */
  style?: React.CSSProperties;
  /** Container ref (for resizing the canvas) */
  containerRef?: React.RefObject<HTMLDivElement>;
  /** Render avatar in greyscale (default: false) */
  greyscale?: boolean;
}

/**
 * Ease-in-out function for smooth transitions
 */
const easeInOut = (t: number): number => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

/**
 * Convert RGB color to greyscale using luminance formula
 * Uses the standard luminance weights: 0.299*R + 0.587*G + 0.114*B
 */
const rgbToGreyscale = (rgb: [number, number, number]): [number, number, number] => {
  const [r, g, b] = rgb;
  const grey = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  return [grey, grey, grey];
};

/**
 * Convert an entire RGBGrid to greyscale
 */
const convertGridToGreyscale = (grid: RGBGrid): RGBGrid => {
  return grid.map(row => row.map(pixel => rgbToGreyscale(pixel)));
};

/**
 * Interpolates between two RGB values using ease-in-out
 */
const interpolateRGB = (from: [number, number, number], to: [number, number, number], progress: number): [number, number, number] => {
  const easedProgress = easeInOut(progress);
  return [
    Math.round(from[0] + (to[0] - from[0]) * easedProgress),
    Math.round(from[1] + (to[1] - from[1]) * easedProgress),
    Math.round(from[2] + (to[2] - from[2]) * easedProgress)
  ];
};

/**
 * Generates transition frames between two avatars using color interpolation
 */
const generateTransitionFrames = (fromAvatar: RGBGrid, toAvatar: RGBGrid, frameCount: number): RGBGrid[] => {
  const frames: RGBGrid[] = [];
  
  for (let frame = 0; frame < frameCount; frame++) {
    const progress = frame / (frameCount - 1);
    const interpolatedAvatar: RGBGrid = fromAvatar.map((row, rowIndex) =>
      row.map((pixel, colIndex) =>
        interpolateRGB(pixel, toAvatar[rowIndex][colIndex], progress)
      )
    );
    frames.push(interpolatedAvatar);
  }
  
  return frames;
};

/**
 * Simplified AnimatedAvatar component that smoothly transitions between hashatar states
 * using color interpolation with ease-in-out timing.
 */
export default function AnimatedAvatar({ 
  text,
  transitionFrames = 20,
  animationSpeed = 1.0,
  size = 32,
  style = {},
  containerRef,
  greyscale = false
}: AnimatedAvatarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previousTextRef = useRef<string>(text);
  const isAnimatingRef = useRef(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentAnimationTextRef = useRef<string>(text);

  // Generate transition frames between previous and current text
  const avatarFrames = useMemo(() => {
    const previousText = previousTextRef.current;
    const currentText = text;
    
    // Generate avatars for previous and current text
    let previousAvatar = generateHashatar(previousText || ' ', { size });
    let currentAvatar = generateHashatar(currentText || ' ', { size });
    
    // Apply greyscale conversion if needed
    if (greyscale) {
      previousAvatar = convertGridToGreyscale(previousAvatar);
      currentAvatar = convertGridToGreyscale(currentAvatar);
    }
    
    // Generate transition frames from previous to current
    const frames = generateTransitionFrames(previousAvatar, currentAvatar, transitionFrames);
    
    return frames;
  }, [text, transitionFrames, size, greyscale]);

  // Clean up animation on component unmount
  useEffect(() => {
    return () => {
      cancelCurrentAnimation();
    };
  }, []);

  /**
   * Renders an avatar directly to the canvas
   */
  const renderAvatarToCanvas = (avatar: RGBGrid) => {
    const canvas = canvasRef.current;
    if (!canvas || !avatar) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set fixed canvas dimensions (256x256 for good quality)
    const canvasSize = 256;
    
    // Set internal canvas dimensions (affects rendering resolution)
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    
    // Set display dimensions (how it appears on screen)
    const containerWidth = containerRef?.current?.clientWidth;
    const containerHeight = containerRef?.current?.clientHeight;
    
    canvas.style.width = (containerWidth && containerWidth > 0) ? `${containerWidth}px` : `${canvasSize}px`;
    canvas.style.height = (containerHeight && containerHeight > 0) ? `${containerHeight}px` : `${canvasSize}px`;

    // Disable anti-aliasing for crisp pixel art
    ctx.imageSmoothingEnabled = false;

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Calculate pixel size (avatar is 32x32)
    const pixelSize = canvasSize / size;

    // Draw each pixel from avatar
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        const [r, g, b] = avatar[row][col];
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        
        const x = Math.floor(col * pixelSize);
        const y = Math.floor(row * pixelSize);
        const width = Math.floor((col + 1) * pixelSize) - x;
        const height = Math.floor((row + 1) * pixelSize) - y;
        
        ctx.fillRect(x, y, width, height);
      }
    }
  };

  /**
   * Cancels the current animation if one is running
   */
  const cancelCurrentAnimation = () => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    isAnimatingRef.current = false;
  };

  /**
   * Animation that transitions from previous to current text
   */
  useEffect(() => {
    if (avatarFrames.length === 0) return;

    // If text changed during animation, cancel current animation and start new one
    if (isAnimatingRef.current && currentAnimationTextRef.current !== text) {
      cancelCurrentAnimation();
      // Immediately render the final frame of the new animation
      renderAvatarToCanvas(avatarFrames[avatarFrames.length - 1]);
      previousTextRef.current = text;
      currentAnimationTextRef.current = text;
      return;
    }

    // Prevent multiple animations for the same text
    if (isAnimatingRef.current && currentAnimationTextRef.current === text) {
      return;
    }

    // Cancel any existing animation before starting new one
    cancelCurrentAnimation();

    isAnimatingRef.current = true;
    currentAnimationTextRef.current = text;
    let frameIndex = 0;

    const runAnimation = () => {
      // Check if animation was cancelled
      if (!isAnimatingRef.current || currentAnimationTextRef.current !== text) {
        return;
      }

      if (frameIndex < avatarFrames.length) {
        renderAvatarToCanvas(avatarFrames[frameIndex]);
        frameIndex++;
        
        const frameDelay = Math.max(16, 50 / animationSpeed);
        animationTimeoutRef.current = setTimeout(runAnimation, frameDelay);
      } else {
        // Animation complete
        isAnimatingRef.current = false;
        animationTimeoutRef.current = null;
        previousTextRef.current = text;
      }
    };

    runAnimation();

    return () => {
      cancelCurrentAnimation();
    };
  }, [avatarFrames, animationSpeed, size, text]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        imageRendering: 'pixelated',
        ...style,
      }}
    />
  );
}
